import { Action } from 'redux';
import { DeadPigletsEvent, IDeadPigletsEvent } from 'shared/api/api';
import {
	mergeArrays,
	removeMultipleValuesFromArray,
	removeValueFromArray,
	upsertValueInArray,
} from 'shared/helpers/reducer-helpers';
import { SyncableInitialState } from 'shared/state/models/syncable';
import { isType } from 'typescript-fsa';
import { siteChange } from '../profile/actions';
import * as actions from './actions';
import { getSyncDataDeadPigletsEvent } from './actions';
import { DeadPigletsEventEventState } from './types';
import { fetchSingleAnimalInfo } from '../stem-animals/actions';
import { getSyncModelData } from '../sync/actions';

export const initialState: DeadPigletsEventEventState = {
	...SyncableInitialState,
	departuredDeadPigletEvents: [],
	analysisDeadPigletGraphData: [],
	analysisLoading: false,
};

const deadPigletsEventEventReducer = (
	state: DeadPigletsEventEventState = initialState,
	action: Action,
): DeadPigletsEventEventState => {
	if (isType(action, getSyncModelData.done)) {
		if (action.payload.params.siteId === action.payload.params.activeSiteId) {
			if (action.payload.result.deadPigletsEvents && action.payload.result.deadPigletsEvents.datas!.length) {
				state = {
					...state,
					entities: mergeArrays(state.entities, action.payload.result.deadPigletsEvents.datas!),
					lastSyncDate: action.payload.result.deadPigletsEvents.syncTime!,
				};
			}
		}
		state = {
			...state,
			syncInProgress: false,
		};
	}

	if (isType(action, getSyncDataDeadPigletsEvent.started)) {
		state = { ...state, syncInProgress: true };
	}

	if (isType(action, getSyncDataDeadPigletsEvent.done)) {
		if (action.payload.params.siteId === action.payload.params.activeSiteId) {
			if (action.payload.result.datas!.length) {
				state = {
					...state,
					entities: mergeArrays(state.entities, action.payload.result.datas!),
					lastSyncDate: action.payload.result.syncTime!,
				};
			}
		}
		state = {
			...state,
			syncInProgress: false,
		};
	}

	if (isType(action, getSyncDataDeadPigletsEvent.failed)) {
		state = { ...state, syncInProgress: false };
	}

	if (isType(action, actions.saveDeadPigletsEvent.started)) {
		if (action.payload && action.payload.isDeleted && action.payload.id) {
			state = {
				...state,
				entities: removeValueFromArray(state.entities, action.payload.id),
				updates: upsertValueInArray(state.updates, action.payload),
				saveSyncInProgress: true,
			};
		} else if (action.payload) {
			state = {
				...state,
				entities: upsertValueInArray(state.entities, action.payload),
				updates: upsertValueInArray(state.updates, action.payload),
				saveSyncInProgress: true,
			};
		}
	}

	if (isType(action, actions.saveDeadPigletsEvent.failed)) {
		if (action.payload.error.code === 500 && action.payload.params) {
			if (action.payload.error.prevEntity) {
				state = { ...state, entities: upsertValueInArray(state.entities, action.payload.error.prevEntity) };
			} else {
				state = { ...state, entities: removeValueFromArray(state.entities, action.payload.params.id!) };
			}
			state = { ...state, updates: removeValueFromArray(state.updates, action.payload.params.id!) };
		}

		state = { ...state, saveSyncInProgress: false };
		return state;
	}

	if (isType(action, actions.saveDeadPigletsEvent.done)) {
		state = { ...state, updates: removeValueFromArray(state.updates, action.payload.result) };
		state = { ...state, saveSyncInProgress: false };
		return state;
	}

	if (isType(action, siteChange.done)) {
		state = initialState;
	}

	if (isType(action, actions.removeDeadPigletsEvent.started)) {
		const idsToRemove = state.entities
			.filter((deadPigletsEvent: any) => deadPigletsEvent.stemAnimalId === action.payload)
			.map((deadPigletsEvent: any) => deadPigletsEvent.id!);
		if (idsToRemove && idsToRemove.length > 0) {
			state = {
				...state,
				entities: removeMultipleValuesFromArray(state.entities, idsToRemove),
			};
		}
	}

	if (isType(action, actions.getDeparturedDeadPigletsEvent.done)) {
		if (action.payload.params.siteId === action.payload.params.activeSiteId) {
			state = {
				...state,
				departuredDeadPigletEvents: action.payload.result,
			};
		}
	}

	if (isType(action, actions.getPigletEventsByPeriod.done)) {
		state = {
			...state,
			entities: action.payload.result,
			lastSyncDate: new Date(-8640000000000000),
		};
	}

	if (isType(action, fetchSingleAnimalInfo.done)) {
		if (action.payload.result.stemAnimal && action.payload.result.deadPigletEvents) {
			if (
				action.payload.result.stemAnimal.departureDate &&
				action.payload.result.stemAnimal.departureDate <= new Date()
			) {
				state = {
					...state,

					departuredDeadPigletEvents: mergeArrays(
						state.departuredDeadPigletEvents,
						action.payload.result.deadPigletEvents,
					),
				};
			} else {
				state = {
					...state,

					entities: mergeArrays(state.entities, action.payload.result.deadPigletEvents),
				};
			}
		}
	}

	if (isType(action, actions.getAnalysisGraphBySiteIdAndDates.started)) {
		state = {
			...state,
			analysisLoading: true,
		};
	}

	if (isType(action, actions.getAnalysisGraphBySiteIdAndDates.failed)) {
		state = {
			...state,
			analysisLoading: false,
		};
	}

	if (isType(action, actions.getAnalysisGraphBySiteIdAndDates.done)) {
		state = {
			...state,
			analysisLoading: false,
			analysisDeadPigletGraphData: action.payload.result,
		};
	}

	//Ensure Date objects are in fact date and not strings - redux-persist serializes dates to string but doesn't deserialize to dates again
	if (action.type === 'persist/REHYDRATE') {
		let a = (action as any) as { payload: DeadPigletsEventEventState; key: string };

		if (a.key === 'deadPigletsEvents') {
			let entities = new Array<DeadPigletsEvent>();
			let updates = new Array<DeadPigletsEvent>();
			let lastSyncDate = SyncableInitialState.lastSyncDate;

			if (a.payload && a.payload && a.payload.entities) {
				entities = a.payload.entities.map((event: IDeadPigletsEvent) => {
					let newEvent = DeadPigletsEvent.fromJS({});
					newEvent.init(event);
					return newEvent;
				});
			}

			if (a.payload && a.payload && a.payload.updates) {
				updates = a.payload.updates.map((event: IDeadPigletsEvent) => {
					let newEvent = DeadPigletsEvent.fromJS({});
					newEvent.init(event);
					return newEvent;
				});
			}

			if (a.payload && a.payload && a.payload.lastSyncDate) {
				lastSyncDate = new Date(a.payload.lastSyncDate);
			}

			state = { ...state, entities, updates, lastSyncDate };
		}
	}

	return state;
};

export default deadPigletsEventEventReducer;
